// Content Templates Service
// Provides ready-to-use content brief templates for campaigns

export const CONTENT_TEMPLATES = {
  PRODUCT_REVIEW: {
    id: 'product_review',
    name: 'Product Review Post',
    description: 'Most popular - Works for any product campaign',
    icon: '📸',
    category: 'General',
    contentStructure: `Photo 1: Product in lifestyle setting
Photo 2-3: Close-up details of key features
Photo 4: You using/wearing the product
Photo 5: Final recommendation shot`,
    
    captionTemplate: `[Hook - Why you tried this product]

[Your honest experience - 2-3 sentences about using the product]

What I loved:
• [Feature 1]
• [Feature 2]
• [Feature 3]

[Your recommendation - Would you recommend it? Why?]

[Call to action - e.g., "Check it out!" or "Link in bio"]

#[BrandHashtag] #ProductReview #ad`,
    
    requirements: [
      '3-5 high-quality photos',
      'Natural lighting preferred',
      'Show product clearly in use',
      'Include yourself in photos',
      'Write 100-200 word caption',
      'Use required hashtags',
      'Tag brand account',
      'Include tracking link in bio or story',
      'Disclose partnership (#ad or #sponsored)'
    ],
    
    dos: [
      'Be authentic and honest in your review',
      'Show product in real-life use',
      'Use natural lighting for photos',
      'Engage with comments within first hour',
      'Post during peak hours (10am-2pm, 6pm-9pm)',
      'Share your genuine experience',
      'Highlight specific features you liked'
    ],
    
    donts: [
      'Over-edit photos to unrealistic levels',
      'Make false or exaggerated claims',
      'Show competitor products in same post',
      'Copy content from other influencers',
      'Post during off-peak hours',
      'Ignore comments from your audience',
      'Forget to disclose the partnership'
    ],
    
    postingTips: `📅 Best Days: Tuesday-Thursday
⏰ Best Times: 10am-2pm, 6pm-9pm
💬 Respond to comments within 1 hour
📊 Use 10-15 relevant hashtags
🔗 Add tracking link to bio or story
📱 Use Instagram Stories to drive engagement
✨ Engage with your audience authentically`,
    
    platformSpecific: {
      instagram: 'Use carousel format for multiple product shots. Add tracking link to bio and mention in caption.',
      tiktok: 'Create a 30-60 second video showing product unboxing and first impressions.',
      facebook: 'Write a longer, detailed review with multiple photos. Great for in-depth product analysis.'
    }
  },
  
  UNBOXING: {
    id: 'unboxing',
    name: 'Unboxing Experience',
    description: 'Perfect for new product launches and first impressions',
    icon: '📦',
    category: 'Launch',
    contentStructure: `Video Format (30-60 seconds):
• Opening: Show sealed package
• Unboxing: Reveal contents with excitement
• First impressions: Initial reactions
• Details: Show key features up close
• Verdict: Your immediate thoughts

Photo Format:
• Photo 1: Sealed package
• Photo 2: Opening moment (hands opening box)
• Photo 3: Contents laid out beautifully
• Photo 4: Product close-ups
• Photo 5: Your reaction/verdict`,
    
    captionTemplate: `Unboxing time! 📦✨

[What you received and why you're excited about it]

First impressions:
• [Packaging quality]
• [Product quality/appearance]
• [Standout features that caught your eye]

[Your initial verdict - excited to try it? Why?]

Swipe to see everything inside! →

#Unboxing #[BrandName] #FirstImpressions #ad`,
    
    requirements: [
      'Show sealed/unopened package first',
      'Capture the unboxing moment',
      'Display all contents clearly',
      'Share genuine first impressions',
      'Good lighting for video/photos',
      'Enthusiastic and authentic reactions',
      'Tag brand and use hashtags',
      'Include tracking link',
      'Disclose partnership'
    ],
    
    dos: [
      'Show genuine excitement and reactions',
      'Display packaging and presentation',
      'Highlight what makes product special',
      'Share immediate first impressions',
      'Use good lighting and clear audio (video)',
      'Create anticipation and excitement',
      'Show all included items'
    ],
    
    donts: [
      'Rush through the unboxing',
      'Use poor lighting or shaky footage',
      'Fake your reactions',
      'Skip showing the packaging',
      'Forget to show all contents',
      'Make it too long and boring',
      'Neglect audio quality (for video)'
    ],
    
    postingTips: `📹 Video: Keep it 30-60 seconds for best engagement
📸 Photos: Use carousel to show unboxing sequence
⏰ Post in the morning (9am-11am) for maximum reach
💬 Ask audience what they want to see next
🎬 Add text overlays highlighting key features
✨ Use trending audio (TikTok/Reels)`,
    
    platformSpecific: {
      instagram: 'Use Reels for unboxing videos. Create carousel posts for photo sequence.',
      tiktok: 'Perfect platform for unboxing videos. Use trending sounds and quick cuts.',
      facebook: 'Longer unboxing videos work well. Include detailed description in caption.'
    }
  },
  
  LIFESTYLE: {
    id: 'lifestyle',
    name: 'Lifestyle Integration',
    description: 'Great for fashion, home decor, and lifestyle products',
    icon: '🎨',
    category: 'Lifestyle',
    contentStructure: `Show product naturally integrated into your daily life:

Photo 1: Wide shot - product in your space/environment
Photo 2: Medium shot - you with product in natural setting
Photo 3: Close-up - product details and quality
Photo 4: Action shot - product in use during daily routine
Photo 5: Lifestyle shot - product enhancing your life`,
    
    captionTemplate: `[Lifestyle context - describe your day, routine, or moment]

[How this product fits naturally into your life]

Why it works for me:
• [Benefit 1 - how it improves your daily life]
• [Benefit 2 - specific use case]
• [Benefit 3 - why you love it]

[Personal recommendation and how others can use it]

#Lifestyle #[BrandName] #DailyEssentials #ad`,
    
    requirements: [
      'Show product in natural, real-life setting',
      'Include yourself in lifestyle context',
      'Demonstrate actual use in daily routine',
      'Use authentic, unforced scenarios',
      'Natural lighting and candid moments',
      'Show how product enhances your life',
      'Tag brand and use hashtags',
      'Include tracking link',
      'Disclose partnership'
    ],
    
    dos: [
      'Show product in your real environment',
      'Be natural and authentic',
      'Demonstrate genuine use cases',
      'Share personal stories',
      'Use soft, natural lighting',
      'Create aspirational but relatable content',
      'Show product from multiple angles'
    ],
    
    donts: [
      'Stage overly perfect, unrealistic scenes',
      'Force product into unnatural situations',
      'Use harsh or artificial lighting',
      'Make it look like an advertisement',
      'Forget to show yourself using it',
      'Overcomplicate the setup',
      'Neglect the lifestyle context'
    ],
    
    postingTips: `📸 Use natural light for authentic feel
🏠 Show product in your actual space
⏰ Post during lifestyle browsing hours (7am-9am, 7pm-9pm)
💭 Tell a story, not just show product
🎨 Maintain your aesthetic while featuring product
✨ Create mood and atmosphere`,
    
    platformSpecific: {
      instagram: 'Perfect for feed posts and Stories. Use aesthetic that matches your profile.',
      tiktok: '"Day in my life" or "Get ready with me" format works great.',
      facebook: 'Share longer stories about how product fits into your lifestyle.'
    }
  },
  
  TUTORIAL: {
    id: 'tutorial',
    name: 'Tutorial/How-To',
    description: 'Perfect for beauty, tech, and educational content',
    icon: '📹',
    category: 'Educational',
    contentStructure: `Step-by-step guide format:

Introduction: What you'll teach and why
Step 1: [First step with clear instruction]
Step 2: [Second step with visual demonstration]
Step 3: [Third step with tips]
Step 4: [Fourth step with common mistakes to avoid]
Final Result: Show the end result

Photo/Video: Show each step clearly with close-ups`,
    
    captionTemplate: `How to [achieve specific result] using [product name]! 📚

Here's my step-by-step guide:

1️⃣ [Step 1 - clear instruction]
2️⃣ [Step 2 - what to do next]
3️⃣ [Step 3 - important tip]
4️⃣ [Step 4 - final touch]

Pro tips:
• [Helpful tip 1]
• [Helpful tip 2]
• [Common mistake to avoid]

[Your results and recommendation]

Save this for later! 💾

#Tutorial #HowTo #[BrandName] #[ProductCategory] #ad`,
    
    requirements: [
      'Clear step-by-step instructions',
      'Show each step visually',
      'Include close-up shots of details',
      'Provide helpful tips and tricks',
      'Show final result clearly',
      'Make it easy to follow',
      'Tag brand and use hashtags',
      'Include tracking link',
      'Disclose partnership'
    ],
    
    dos: [
      'Break down into simple steps',
      'Show close-ups of technique',
      'Provide helpful tips',
      'Demonstrate clearly',
      'Use good lighting for details',
      'Speak clearly (for video)',
      'Show common mistakes to avoid',
      'Display final results'
    ],
    
    donts: [
      'Rush through steps',
      'Skip important details',
      'Use poor lighting for demonstrations',
      'Make it too complicated',
      'Forget to show the final result',
      'Use confusing terminology',
      'Assume prior knowledge'
    ],
    
    postingTips: `📹 Video: 60-90 seconds for detailed tutorials
📸 Carousel: Perfect for step-by-step photos
⏰ Post when audience has time to watch (evenings, weekends)
💬 Encourage questions in comments
📌 Make it saveable content
🎬 Add text overlays for each step
✨ Use clear, simple language`,
    
    platformSpecific: {
      instagram: 'Use Reels for quick tutorials, carousel for detailed step-by-step.',
      tiktok: 'Fast-paced tutorials work best. Use text overlays and trending audio.',
      facebook: 'Longer, detailed tutorials with comprehensive explanations.'
    }
  },
  
  BEFORE_AFTER: {
    id: 'before_after',
    name: 'Before/After Transformation',
    description: 'Ideal for beauty, fitness, and home improvement',
    icon: '✨',
    category: 'Transformation',
    contentStructure: `Show transformation journey:

Photo 1: "Before" state (clear, well-lit)
Photo 2: Product/process in action
Photo 3: Midway progress (if applicable)
Photo 4: "After" result (same lighting/angle as before)
Photo 5: Close-up of results

Use split-screen or side-by-side for dramatic effect`,
    
    captionTemplate: `The results speak for themselves! ✨

Before: [Describe starting point]
After: [Describe results after using product]

My experience:
[How long you used it, what you noticed, how you felt]

What changed:
• [Visible change 1]
• [Visible change 2]
• [How you feel about results]

Timeline: [How long it took to see results]

[Your honest recommendation]

#BeforeAndAfter #Transformation #[BrandName] #Results #ad`,
    
    requirements: [
      'Clear "before" photo with good lighting',
      'Matching "after" photo (same angle, lighting)',
      'Show product/process in between',
      'Be honest about timeline',
      'Use same background/setting',
      'Show realistic results',
      'Tag brand and use hashtags',
      'Include tracking link',
      'Disclose partnership'
    ],
    
    dos: [
      'Use consistent lighting for both photos',
      'Take photos from same angle',
      'Be honest about timeline',
      'Show realistic, unedited results',
      'Explain the process',
      'Share your genuine experience',
      'Highlight specific improvements'
    ],
    
    donts: [
      'Use different lighting to exaggerate results',
      'Change angles between before/after',
      'Edit photos to fake results',
      'Make unrealistic claims',
      'Rush the timeline',
      'Use misleading comparisons',
      'Forget to mention how long it took'
    ],
    
    postingTips: `📸 Use same lighting and angle for fair comparison
⏰ Post when audience is most engaged
💬 Share your honest journey in caption
📊 Include timeline for realistic expectations
✨ Use split-screen or carousel format
🎯 Focus on specific, visible changes
💡 Explain what you did to achieve results`,
    
    platformSpecific: {
      instagram: 'Carousel posts work great for before/after. Use Stories for progress updates.',
      tiktok: 'Time-lapse or transition videos are very engaging.',
      facebook: 'Detailed posts with full journey story perform well.'
    }
  }
};

// Get all templates as array
export const getAllTemplates = () => {
  return Object.values(CONTENT_TEMPLATES);
};

// Get template by ID
export const getTemplateById = (templateId) => {
  return Object.values(CONTENT_TEMPLATES).find(t => t.id === templateId) || null;
};

// Get templates by category
export const getTemplatesByCategory = (category) => {
  return Object.values(CONTENT_TEMPLATES).filter(t => t.category === category);
};

// Generate full content brief from template
export const generateContentBrief = (template, brandInfo = {}) => {
  const {
    brandName = '[Brand Name]',
    brandHashtag = '[BrandHashtag]',
    brandHandle = '[BrandHandle]',
    productName = '[Product Name]',
    additionalRequirements = []
  } = brandInfo;
  
  // Replace placeholders in caption template
  let caption = template.captionTemplate
    .replace(/\[BrandName\]/g, brandName)
    .replace(/\[BrandHashtag\]/g, brandHashtag)
    .replace(/\[BrandHandle\]/g, brandHandle)
    .replace(/\[Product Name\]/g, productName);
  
  return {
    templateName: template.name,
    templateId: template.id,
    contentStructure: template.contentStructure,
    captionTemplate: caption,
    requirements: [...template.requirements, ...additionalRequirements],
    dos: template.dos,
    donts: template.donts,
    postingTips: template.postingTips,
    platformSpecific: template.platformSpecific
  };
};

// Format brief for display/storage
export const formatBriefForDisplay = (brief) => {
  return `📋 CONTENT BRIEF - ${brief.templateName}

CONTENT STRUCTURE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.contentStructure}

CAPTION TEMPLATE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.captionTemplate}

REQUIREMENTS CHECKLIST:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.requirements.map(req => `☐ ${req}`).join('\n')}

DO'S:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.dos.map(item => `✅ ${item}`).join('\n')}

DON'TS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.donts.map(item => `❌ ${item}`).join('\n')}

POSTING TIPS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${brief.postingTips}`;
};

export default {
  CONTENT_TEMPLATES,
  getAllTemplates,
  getTemplateById,
  getTemplatesByCategory,
  generateContentBrief,
  formatBriefForDisplay
};
